import React, { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { CreateDoctorDto, DoctorDto, RppsDto } from '@services/api'
import { RppsAutocompleteByNameInput } from '@components/RppsAutocompleteByNameInput'
import { doctorSchema } from '@utils/schemas'
import { doCreateDoctor, doUpdateDoctor } from '@state/reducers/doctorReducer'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { Star, ToggleOffOutlined, ToggleOn } from '@mui/icons-material'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import HomeIcon from '@mui/icons-material/Home'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import { getAddressesByRpps } from '@state/reducers/mssReducer'
import { v4 as uuidv4 } from 'uuid'
import InputField from '../inputs/InputField'
import {
  AddressAutocomplete,
  AddressDTO,
} from '../autocomplete/LocationAutocomplete'

type Props = {
  editingDoctor?: DoctorDto | null
  onClose: () => void
  hideRppsAutocomplete?: boolean
  onDoctorCreated?: (doctor: RppsDto) => void
}

const DoctorForm: React.FC<Props> = ({
  editingDoctor,
  onClose,
  hideRppsAutocomplete = false,
  onDoctorCreated,
}) => {
  const {
    setValue,
    control,
    handleSubmit,
    watch,
    register,
    formState: { errors },
  } = useForm<CreateDoctorDto>({
    resolver: yupResolver(doctorSchema),
    defaultValues: editingDoctor || {
      addresses: [],
      mssEmails: [],
    },
  })

  const dispatch = useAppDispatch()
  const { mssEmailsByRpps, loadingMssEmailsByRpps } = useAppSelector(
    ({ mss }) => ({
      mssEmailsByRpps: mss.addressesByRpps,
      loadingMssEmailsByRpps: mss.loadingAddressesByRpps,
    }),
  )

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'addresses',
  })
  const {
    fields: mssAddressFields,
    append: appendMssAddress,
    remove: removeMssAddress,
  } = useFieldArray({
    control,
    name: 'mssEmails',
  })

  const [rppsValue, setrppsValue] = useState<RppsDto | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [addressActiveTab, setAddressActiveTab] = useState(0)
  const [favoriteAddressIndex, setFavoriteAddressIndex] = useState(0)
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false)
  const [currentAddressIndex, setCurrentAddressIndex] = useState<number | null>(
    null,
  )
  const handleRppsChange = (rpps: RppsDto | null) => {
    setrppsValue(rpps)

    if (!rpps) {
      return
    }

    dispatch(getAddressesByRpps(rpps.rppsNumber))

    setValue('rppsNumber', rpps.rppsNumber)
    setValue('firstName', rpps.firstName)
    setValue('lastName', rpps.lastName)
    setValue('email', rpps.email)
    setValue('skill', rpps.skillLabel)
    setValue('profession', rpps.professionLabel)
  }

  useEffect(() => {
    if (mssEmailsByRpps.length > 0) {
      setValue(
        'mssEmails',
        mssEmailsByRpps.map((email) => ({
          active: true,
          favorite: false,
          email,
        })),
      )
    }
  }, [mssEmailsByRpps])

  const onSubmit = (data: CreateDoctorDto) => {
    setIsSubmitting(true)
    const cb = (msg: string, createdDoctor?: DoctorDto) => {
      dispatch(
        enqueueSnackbar({
          message: msg,
          options: { variant: 'success' },
          type: 'success',
        }),
      )

      if (onDoctorCreated && createdDoctor) {
        const rppsDto = {
          rppsNumber: createdDoctor.rppsNumber,
          firstName: createdDoctor.firstName,
          lastName: createdDoctor.lastName,
          email: createdDoctor.email || '',
          skill: createdDoctor.skill || '',
          profession: createdDoctor.profession || '',
          skillLabel: createdDoctor.skill || '',
          professionLabel: createdDoctor.profession || '',
          title: '',
          id: createdDoctor.id || -1,
          fax: '',
          zipCode: '',
          skillType: '',
          category: '',
        } as RppsDto
        onDoctorCreated(rppsDto)
      } else {
        onClose()
      }
    }

    if (editingDoctor) {
      dispatch(doUpdateDoctor({ id: editingDoctor.id, dto: data }))
        .unwrap()
        .then((updatedDoctor) =>
          cb('Medecin modifié avec succès', updatedDoctor),
        )
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      dispatch(doCreateDoctor(data))
        .unwrap()
        .then((createdDoctor) => cb('Medecin créé avec succès', createdDoctor))
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }

  const handleSetFavorite = (index: number) => {
    setFavoriteAddressIndex(index)
  }

  const handleRemoveAddress = (index: number) => {
    if (index === activeTab) {
      setActiveTab(index > 0 ? index - 1 : 0)
    } else if (index < activeTab) {
      setActiveTab(activeTab - 1)
    }
    if (index === favoriteAddressIndex) {
      handleSetFavorite(0)
    } else if (index < favoriteAddressIndex) {
      handleSetFavorite(favoriteAddressIndex - 1)
    }
    remove(index)
  }

  const handlePreSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const mssEmails = watch('mssEmails')
    mssEmails.forEach((address, index) => {
      if (address.email === '') {
        removeMssAddress(index)
      }
    })
    handleSubmit(onSubmit)()
  }

  const handleOpenAutocomplete = (addressIndex: number) => {
    setCurrentAddressIndex(addressIndex)
    setIsAutocompleteOpen(true)
  }

  const handleAutocompleteSelect = (addressData: AddressDTO) => {
    if (currentAddressIndex !== null) {
      setValue(`addresses.${currentAddressIndex}.line1`, addressData.address)
      setValue(
        `addresses.${currentAddressIndex}.postalCode`,
        addressData.zipCode,
      )
      setValue(`addresses.${currentAddressIndex}.city`, addressData.city)
      setValue(`addresses.${currentAddressIndex}.country`, addressData.country)
    }
    setIsAutocompleteOpen(false)
    setCurrentAddressIndex(null)
  }

  return (
    <div className="h-full flex flex-col">
      <form onSubmit={handlePreSubmit} className="flex flex-col">
        <Paper
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight="600" color="text.primary">
              Informations du médecin
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSubmit(onSubmit)()
                }}
                startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
              >
                Sauvegarder
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Doctor Information Section */}
            <Box
              sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {/* RPPS Search */}
              <RppsAutocompleteByNameInput
                showCreateOption={false}
                onChange={handleRppsChange}
                label="RPPS"
                value={rppsValue}
                size="small"
                hidden={hideRppsAutocomplete}
                showLocalSearchOption={false}
              />

              {/* Main Info Grid */}
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <InputField
                    label="Nom *"
                    name="firstName"
                    control={control}
                    size="small"
                    placeholder="Nom"
                    InputProps={{ sx: { textTransform: 'capitalize' } }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <InputField
                    label="Prénom *"
                    name="lastName"
                    control={control}
                    size="small"
                    placeholder="Prénom"
                    InputProps={{ sx: { textTransform: 'capitalize' } }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <InputField
                    label="Numero RPPS"
                    name="rppsNumber"
                    control={control}
                    size="small"
                    placeholder="Numéro RPPS"
                  />
                </Grid>

                <Grid item xs={3}>
                  <InputField
                    label="Spécialité"
                    name="skill"
                    control={control}
                    size="small"
                    placeholder="Spécialité"
                    InputProps={{ sx: { textTransform: 'capitalize' } }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <InputField
                    label="Email"
                    name="email"
                    control={control}
                    type="email"
                    size="small"
                    placeholder="Email"
                  />
                </Grid>

                <Grid item xs={3}>
                  <InputField
                    label="Téléphone"
                    name="mobileNumber"
                    control={control}
                    size="small"
                    placeholder="Téléphone"
                    InputProps={{ sx: { textTransform: 'capitalize' } }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <InputField
                    label="Fax"
                    name="phoneNumber"
                    control={control}
                    size="small"
                    placeholder="Fax"
                    InputProps={{ sx: { textTransform: 'capitalize' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Addresses Section */}
            <div className="px-6 pb-6 flex-1 flex flex-col min-h-0">
              <Tabs
                value={addressActiveTab}
                onChange={(event: React.SyntheticEvent, activeTab: number) => {
                  setAddressActiveTab(activeTab)
                }}
                sx={{ minHeight: '45px' }}
                className="mb-4"
              >
                <Tab
                  icon={<HomeIcon />}
                  iconPosition="start"
                  label="Adresses postales"
                  sx={{ minHeight: '45px' }}
                />
                <Tab
                  icon={<AlternateEmailIcon />}
                  iconPosition="start"
                  label="Adresses MSS"
                  sx={{ minHeight: '45px' }}
                />
              </Tabs>

              {addressActiveTab === 0 && (
                <>
                  <div className="border-b border-gray-200">
                    <div className="flex space-x-2">
                      {fields.map((field, index) => {
                        const addressName =
                          watch(`addresses.${index}.locationName`) ||
                          `Address ${index + 1}`
                        return (
                          <button
                            key={field.id}
                            type="button"
                            onClick={() => setActiveTab(index)}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                              activeTab === index
                                ? 'bg-indigo-50 text-[#3A0CA1] border-t border-l border-r border-gray-200'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {addressName}
                          </button>
                        )
                      })}
                      {/* Add New Tab Button */}
                      <button
                        type="button"
                        onClick={() => {
                          append({})
                          setActiveTab(fields.length)
                        }}
                        className="px-3 py-2 text-sm font-medium text-[#3A0CA1] rounded-t-lg hover:bg-purple-50 border-t border-l border-r border-transparent hover:border-gray-200"
                      >
                        <LocalHospitalIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto mt-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className={`${
                          activeTab === index ? 'block' : 'hidden'
                        } bg-white rounded-md`}
                      >
                        <div className="relative bg-indigo-50 p-4 rounded-md">
                          {/* Icons positioned absolutely in top-right corner */}
                          <div className="absolute top-2 right-2 flex space-x-2">
                            <Tooltip title="Set as favorite">
                              <button
                                type="button"
                                onClick={() => handleSetFavorite(index)}
                                className={`border border-1 px-1 rounded-sm ${
                                  favoriteAddressIndex === index
                                    ? 'text-yellow-500 border-yellow-500'
                                    : 'text-gray-400 border-gray-200 hover:text-yellow-500'
                                }`}
                              >
                                <Star
                                  fill={
                                    favoriteAddressIndex === index
                                      ? 'currentColor'
                                      : 'none'
                                  }
                                />
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete address">
                              <button
                                type="button"
                                onClick={() => handleRemoveAddress(index)}
                                className="text-red-500 hover:text-red-700 border border-1 px-2 rounded-sm border-gray-200"
                              >
                                <i className="fas fa-times" />
                              </button>
                            </Tooltip>
                          </div>

                          {/* Form Grid */}
                          <div className="grid grid-cols-2 gap-1 mt-6">
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Nom de l&apos;adresse *
                              </label>
                              <div className="flex gap-2">
                                <input
                                  {...register(
                                    `addresses.${index}.locationName`,
                                  )}
                                  className="flex-1 px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Enter location name"
                                />
                                <Button
                                  type="button"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenAutocomplete(index)}
                                  className="whitespace-nowrap"
                                >
                                  📍 Autocomplete
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Type d&apos;établissement
                              </label>
                              <select
                                {...register(`addresses.${index}.locationType`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select type</option>
                                <option value="Clinique">Clinique</option>
                                <option value="Hospital">Hospital</option>
                                <option value="Etablissement">
                                  Etablissement
                                </option>
                              </select>
                            </div>
                            {/* Rest of the form fields */}
                            <div className="col-span-1 space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Adresse ligne 1 *
                              </label>
                              <input
                                {...register(`addresses.${index}.line1`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter address line 1"
                              />
                            </div>
                            <div className="col-span-1 space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Adresse ligne 2
                              </label>
                              <input
                                {...register(`addresses.${index}.line2`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter address line 2"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Code postal *
                              </label>
                              <input
                                {...register(`addresses.${index}.postalCode`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter postal code"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Ville *
                              </label>
                              <input
                                {...register(`addresses.${index}.city`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter city"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Pays *
                              </label>
                              <input
                                {...register(`addresses.${index}.country`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter country"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                FINESS
                              </label>
                              <input
                                {...register(`addresses.${index}.finessNumber`)}
                                className="w-full px-2 py-1 capitalize border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter FINESS number"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {addressActiveTab === 1 && (
                <div className="h-[374px] overflow-y-auto bg-indigo-50 rounded-md p-4">
                  {loadingMssEmailsByRpps ? (
                    <Typography>Récupération des adresses MSS...</Typography>
                  ) : (
                    <>
                      {mssAddressFields.map((_field, index) => (
                        <div
                          className="space-y-1 mb-2"
                          key={`mssAddressField_${uuidv4()}`}
                        >
                          <label className="text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <div className="flex">
                            <input
                              {...register(`mssEmails.${index}.email`)}
                              className="w-full px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter email"
                            />
                            <Tooltip
                              title={
                                watch(`mssEmails.${index}.favorite`)
                                  ? 'Remove favorite'
                                  : 'Set as favorite'
                              }
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  if (!watch(`mssEmails.${index}.favorite`))
                                    setValue(`mssEmails.${index}.active`, true)
                                  setValue(
                                    `mssEmails.${index}.favorite`,
                                    !watch(`mssEmails.${index}.favorite`),
                                  )
                                }}
                                className={`ml-4 border border-1 px-1 rounded-sm ${
                                  watch(`mssEmails.${index}.favorite`)
                                    ? 'text-yellow-500 border-yellow-500'
                                    : 'text-gray-400 border-gray-200 hover:text-yellow-500'
                                }`}
                              >
                                <Star
                                  fill={
                                    watch(`mssEmails.${index}.favorite`)
                                      ? 'currentColor'
                                      : 'none'
                                  }
                                />
                              </button>
                            </Tooltip>

                            <Tooltip
                              title={
                                watch(`mssEmails.${index}.active`)
                                  ? 'Remove active'
                                  : 'Set as active'
                              }
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  if (watch(`mssEmails.${index}.active`))
                                    setValue(
                                      `mssEmails.${index}.favorite`,
                                      false,
                                    )
                                  setValue(
                                    `mssEmails.${index}.active`,
                                    !watch(`mssEmails.${index}.active`),
                                  )
                                }}
                                className={`ml-2 border border-1 px-1 rounded-sm ${
                                  watch(`mssEmails.${index}.active`)
                                    ? 'text-green-500 border-green-500'
                                    : 'text-gray-400 border-gray-200 hover:text-green-500'
                                }`}
                              >
                                {watch(`mssEmails.${index}.active`) ? (
                                  <ToggleOn fill={'currentColor'} />
                                ) : (
                                  <ToggleOffOutlined />
                                )}
                              </button>
                            </Tooltip>

                            <Tooltip title="Delete address">
                              <button
                                type="button"
                                onClick={() => removeMssAddress(index)}
                                className="ml-4 text-red-500 hover:text-red-700 border border-1 px-2 rounded-sm border-gray-200"
                              >
                                <i className="fas fa-times" />
                              </button>
                            </Tooltip>
                          </div>
                          {errors.mssEmails?.[index]?.email?.message && (
                            <p className="text-xs text-red-500">
                              {errors.mssEmails?.[index]?.email?.message}
                            </p>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          appendMssAddress({
                            active: true,
                            favorite: false,
                            email: '',
                          })
                        }}
                        className="mt-2 px-3 py-2 text-sm font-medium text-[#3A0CA1] bg-white rounded-lg hover:bg-purple-50 border-transparent hover:border-gray-200"
                      >
                        <LocalHospitalIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </Box>
        </Paper>
      </form>

      {/* Google Places Autocomplete Modal */}
      <AddressAutocomplete
        isOpen={isAutocompleteOpen}
        onClose={() => {
          setIsAutocompleteOpen(false)
          setCurrentAddressIndex(null)
        }}
        onChange={handleAutocompleteSelect}
        apiKey={process.env.REACT_APP_PLACE_API || ''}
        zipCode=""
        city=""
      />
    </div>
  )
}

export default DoctorForm
